<template>
    <div class="p-4 max-w-6xl mx-auto space-y-6">
        <!-- Search -->
        <input
            v-model="search"
            type="text"
            :placeholder="translations.placeholder"
            class="w-full px-4 py-2 border border-slate-500 rounded-2sm text-black placeholder:text-black/50"
        />

        <!-- Vocabulary -->
        <div class="space-y-4">
            <!-- Column Header Arrows (centered) -->
            <div class="grid grid-cols-2 gap-6 items-center mb-2">
                <div class="flex justify-center">
                    <ArrowIcon
                        :asc="orderAsc"
                        :active="orderBy === 'ru'"
                        color="accent"
                        @click="toggleOrder('ru')"
                    />
                </div>
                <div class="flex justify-center">
                    <ArrowIcon
                        :asc="orderAsc"
                        :active="orderBy === 'lt'"
                        color="secondary"
                        @click="toggleOrder('lt')"
                    />
                </div>
            </div>

            <!-- Word rows -->
            <!-- Word rows -->
            <div
                v-for="item in orderedVocabulary"
                :key="item.id"
                class="grid grid-cols-1 md:grid-cols-2 gap-6 md:items-stretch"
            >
                <!-- Merged Card for Mobile -->
                <div
                    class="block md:hidden bg-gardenia/20 p-4 border rounded-2sm border-slate-200 shadow-sm"
                >
                    <div class="text-xl font-bold text-accent">
                        {{ item.russian }}
                    </div>
                    <div
                        v-if="item.description?.ru"
                        class="mt-1 text-sm text-black/80"
                    >
                        {{ item.description.ru }}
                    </div>

                    <hr class="my-2 border-slate-300" />

                    <div class="text-xl font-bold text-secondary">
                        {{ item.lithuanian }}
                    </div>
                    <div
                        v-if="item.description?.lt"
                        class="mt-1 text-sm text-black/80"
                    >
                        {{ item.description.lt }}
                    </div>
                </div>

                <!-- Split Cards for Desktop with Equal Height -->
                <div
                    class="hidden md:block bg-gardenia/20 p-4 border rounded-2sm border-slate-200 shadow-sm h-full"
                >
                    <div class="text-xl font-bold text-accent">
                        {{ item.russian }}
                    </div>
                    <div
                        v-if="item.description?.ru"
                        class="mt-2 text-sm text-black/80"
                    >
                        {{ item.description.ru }}
                    </div>
                </div>

                <div
                    class="hidden md:block bg-gardenia/20 p-4 border rounded-2sm border-slate-200 shadow-sm h-full"
                >
                    <div class="text-xl font-bold text-secondary">
                        {{ item.lithuanian }}
                    </div>
                    <div
                        v-if="item.description?.lt"
                        class="mt-2 text-sm text-black/80"
                    >
                        {{ item.description.lt }}
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { ref, computed } from "vue";
import ArrowIcon from "./ArrowIcon.vue";

interface VocabularyItem {
    id: string;
    russian: string;
    lithuanian: string;
    description?: {
        ru?: string;
        lt?: string;
    };
}

const props = defineProps<{
    words: VocabularyItem[];
    translations: {
        placeholder: string;
    };
}>();

const search = ref("");
const orderBy = ref<"ru" | "lt" | null>(null);
const orderAsc = ref(true);

const toggleOrder = (lang: "ru" | "lt") => {
    if (orderBy.value === lang) {
        orderAsc.value = !orderAsc.value;
    } else {
        orderBy.value = lang;
        orderAsc.value = true;
    }
};

const filteredVocabulary = computed(() =>
    props.words.filter((item) =>
        [
            item.russian,
            item.lithuanian,
            item.description?.ru ?? "",
            item.description?.lt ?? "",
        ]
            .join(" ")
            .toLowerCase()
            .includes(search.value.toLowerCase()),
    ),
);

const orderedVocabulary = computed(() => {
    if (!orderBy.value) return filteredVocabulary.value;

    const field = orderBy.value === "ru" ? "russian" : "lithuanian";

    const sorted = [...filteredVocabulary.value].sort((a, b) =>
        a[field].localeCompare(b[field]),
    );

    return orderAsc.value ? sorted : sorted.reverse();
});
</script>
